// src/pages/MesMissions.jsx
// Vue CLIENT « Mes missions » — kanban lecture seule. N'affiche que les missions que
// l'Art Advisor a marquées « visibles » (via RPC sécurisée get_client_missions, qui masque
// les notes internes et filtre sur auth.uid()). Le client ne modifie rien.
import React, { useEffect, useState } from "react";
import { supabase } from "../lib/supabaseClient";
import { MISSION_TYPES, MISSION_STATUSES, labelOf, statusColor, BADGE } from "../config/advisorMissions";

const COLUMNS = [
  { key: "a_venir", label: "À venir", statuses: ["draft", "proposed", "accepted"] },
  { key: "en_cours", label: "En cours", statuses: ["in_progress", "waiting_client", "waiting_third_party"] },
  { key: "a_livrer", label: "À livrer", statuses: ["ready_to_deliver"] },
  { key: "termine", label: "Terminé", statuses: ["delivered", "closed"] },
];

const fmt = (d) => (d ? new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }) : null);

export default function MesMissions() {
  const [missions, setMissions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const { data, error } = await supabase.rpc("get_client_missions");
      if (error) console.error("get_client_missions", error);
      setMissions((data || []).filter((m) => m.status !== "cancelled"));
      setLoading(false);
    })();
  }, []);

  const byColumn = (col) => missions.filter((m) => col.statuses.includes(m.status));

  return (
    <div className="min-h-screen bg-[#f9f7f4] p-6 lg:p-8">
      <div className="mx-auto max-w-[1200px]">
        <div className="mb-1">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#c9a961]" style={{ fontFamily: "Georgia, serif" }}>Suivi</p>
          <h1 className="font-serif text-3xl font-semibold text-gray-950" style={{ fontFamily: "Georgia, serif" }}>Mes missions</h1>
        </div>
        <p className="mb-6 max-w-2xl text-sm text-gray-500">
          Le suivi des missions confiées à votre conseiller. Cette vue est en lecture seule — votre conseiller met à jour l'avancement.
        </p>

        {loading ? (
          <div className="py-16 text-center text-gray-400">Chargement…</div>
        ) : missions.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-10 text-center text-gray-500">
            Aucune mission partagée pour le moment.
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {COLUMNS.map((col) => {
              const items = byColumn(col);
              return (
                <div key={col.key}>
                  <div className="mb-3 flex items-center justify-between border-b-2 border-gray-900 pb-2">
                    <span className="text-sm font-semibold text-gray-900">{col.label}</span>
                    <span className="text-xs text-gray-400">{items.length}</span>
                  </div>
                  <div className="flex flex-col gap-3">
                    {items.length === 0 ? (
                      <div className="rounded-xl border border-dashed border-gray-200 p-4 text-center text-xs italic text-gray-300">—</div>
                    ) : items.map((m) => (
                      <div key={m.id} className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                        <div className="text-[15px] font-medium text-gray-900" style={{ fontFamily: "Georgia, serif" }}>{m.title}</div>
                        <div className="mt-1 text-xs text-gray-400">{labelOf(MISSION_TYPES, m.mission_type)}</div>
                        <div className="mt-3 flex items-center justify-between gap-2">
                          <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-medium ${BADGE[statusColor(m.status)]}`}>
                            {labelOf(MISSION_STATUSES, m.status)}
                          </span>
                          {fmt(m.due_at) && <span className="text-[11px] text-gray-400">Échéance : {fmt(m.due_at)}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
